import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, RotateCw, Search } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Screen } from '@/ui/Screen'
import { AdminLockButton } from '@/features/admin/AdminLockButton'
import { Button } from '@/ui/Button'
import { cx } from '@/lib/cx'
import type { ScanRecord, ScanStatus } from '@/lib/api-types'

const PAGE_SIZE = 25
const STATUS_TABS: { value: ScanStatus | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'failed', label: 'Failed' },
  { value: 'success', label: 'Sent' },
]

/**
 * Scan history.
 *
 * Filtering and paging happen in SQL, not here. The old app loaded every record
 * into the browser and filtered it in a `useMemo`, so opening History got
 * slower with every scan ever taken and eventually became the slowest screen in
 * the app.
 */
export function HistoryScreen() {
  const [scans, setScans] = useState<ScanRecord[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [status, setStatus] = useState<ScanStatus | null>(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  // Identifies the newest query so a slow earlier response cannot overwrite the
  // results of a newer one — the classic search race.
  const queryId = useRef(0)

  const load = useCallback(
    async (opts: { status: ScanStatus | null; search: string; append?: string | null }) => {
      const id = ++queryId.current
      const appending = opts.append != null

      if (appending) setLoadingMore(true)
      else setLoading(true)
      setError(null)

      try {
        const result = await api.getHistory({
          status: opts.status,
          search: opts.search,
          cursor: opts.append ?? null,
          limit: PAGE_SIZE,
        })
        if (id !== queryId.current) return

        setScans((current) => (appending ? [...current, ...result.scans] : result.scans))
        setCursor(result.nextCursor)
      } catch (err) {
        if (id !== queryId.current) return
        setError(err instanceof ApiError ? err.message : 'Could not load history.')
      } finally {
        if (id === queryId.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [],
  )

  // Debounced so typing a ticket number doesn't fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load({ status, search }), search === '' ? 0 : 300)
    return () => clearTimeout(timer)
  }, [status, search, load])

  const retry = async (scan: ScanRecord) => {
    setRetryingId(scan.id)
    try {
      const result = await api.retryScan(scan.id)
      // Patch the row in place rather than reloading: a full refetch would
      // reorder the list under the user's thumb.
      setScans((current) => current.map((s) => (s.id === scan.id ? result.scan : s)))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Retry failed.')
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <Screen title="History" subtitle="Every scan, and whether it landed." action={<AdminLockButton />}>
      <div className="space-y-3">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--text-muted)]"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search codes and fields"
            aria-label="Search scans"
            className="min-h-touch w-full rounded-2xl border border-[var(--border-input)] bg-[var(--surface-raised)] pl-12 pr-4 outline-none focus:border-brand-600"
          />
        </div>

        <div role="tablist" aria-label="Filter by status" className="flex gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.label}
              role="tab"
              aria-selected={status === tab.value}
              onClick={() => setStatus(tab.value)}
              className={cx(
                'min-h-touch flex-1 rounded-2xl border text-sm font-semibold transition-colors',
                status === tab.value
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] text-[var(--text-secondary)]',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error !== null && (
        <p role="alert" className="mt-4 rounded-2xl bg-bad-50 p-3 text-[0.9375rem] text-bad-700">
          {error}
        </p>
      )}

      <div className="mt-4">
        {loading ? (
          <ul className="space-y-2" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <li
                key={i}
                className="h-20 animate-pulse rounded-[var(--radius-card)] bg-[var(--surface-sunken)]"
              />
            ))}
          </ul>
        ) : scans.length === 0 ? (
          // Suppressed when the load failed: "No scans yet" states an outcome
          // we did not actually learn, directly under a message saying so.
          error !== null ? null : (
            <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-strong)] px-4 py-8 text-center text-[0.9375rem] text-[var(--text-secondary)]">
              {search !== '' || status !== null
                ? 'No scans match this filter.'
                : 'No scans yet. They will appear here as you go.'}
            </p>
          )
        ) : (
          <>
            <ul className="space-y-2">
              {scans.map((scan) => (
                <li key={scan.id}>
                  <ScanRow
                    scan={scan}
                    retrying={retryingId === scan.id}
                    onRetry={() => void retry(scan)}
                  />
                </li>
              ))}
            </ul>

            {cursor !== null && (
              <Button
                variant="secondary"
                fullWidth
                className="mt-4"
                busy={loadingMore}
                onClick={() => void load({ status, search, append: cursor })}
              >
                Load older scans
              </Button>
            )}
          </>
        )}
      </div>
    </Screen>
  )
}

function ScanRow({
  scan,
  retrying,
  onRetry,
}: {
  scan: ScanRecord
  retrying: boolean
  onRetry: () => void
}) {
  const failed = scan.status === 'failed'

  return (
    <div
      className={cx(
        'rounded-[var(--radius-card)] border p-3',
        failed
          ? 'border-bad-500/40 bg-bad-50'
          : 'border-[var(--border-subtle)] bg-[var(--surface-raised)]',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cx(
            'grid h-9 w-9 shrink-0 place-items-center rounded-full',
            scan.status === 'success'
              ? 'bg-ok-50 text-ok-600'
              : failed
                ? 'bg-bad-100 text-bad-600'
                : 'bg-[var(--surface-sunken)] text-[var(--text-muted)]',
          )}
        >
          {scan.status === 'success' ? (
            <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
          ) : failed ? (
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Clock className="h-5 w-5" aria-hidden="true" />
          )}
          <span className="sr-only">{scan.status}</span>
        </span>

        <div className="min-w-0 flex-1">
          <p className="break-all font-mono text-[0.9375rem]">{scan.code}</p>
          <p className="mt-0.5 text-sm text-[var(--text-muted)]">
            {scan.workflowName ?? 'Deleted workflow'} ·{' '}
            <time dateTime={new Date(scan.scannedAt).toISOString()}>
              {new Date(scan.scannedAt).toLocaleString([], {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
            {scan.deviceName !== null && ` · ${scan.deviceName}`}
          </p>

          {failed && scan.error !== null && (
            <p className="mt-2 text-sm text-bad-700">{scan.error}</p>
          )}

          {failed && (
            <Button
              variant="danger"
              className="mt-3"
              busy={retrying}
              leadingIcon={<RotateCw className="h-4 w-4" aria-hidden="true" />}
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
